import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  Search,
  RefreshCw,
  Code2,
  ListChecks,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fields that should be hidden by default in the table (still editable)
const HIDDEN_COLS = new Set(["_id", "password"]);

// Utility: detect input type from a value
const detectType = (value) => {
  if (value === null || value === undefined) return "string";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (Array.isArray(value) || typeof value === "object") return "json";
  const s = String(value);
  if (s.length > 120 || s.includes("\n")) return "textarea";
  return "string";
};

const formatCellValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return "{…}";
  const s = String(value);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
};

// -------------------- Auto-generated form --------------------
const RecordForm = ({ record, onChange, mode }) => {
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(record, null, 2)
  );
  const [jsonError, setJsonError] = useState(null);

  // Sync jsonText when switching to json mode or when record changes externally
  useEffect(() => {
    if (mode === "json") {
      setJsonText(JSON.stringify(record, null, 2));
      setJsonError(null);
    }
  }, [mode, record]);

  const handleFieldChange = (key, newValue, type) => {
    let coerced = newValue;
    if (type === "number") {
      coerced = newValue === "" ? "" : Number(newValue);
      if (Number.isNaN(coerced)) coerced = newValue;
    } else if (type === "boolean") {
      coerced = Boolean(newValue);
    } else if (type === "json") {
      try {
        coerced = JSON.parse(newValue);
      } catch {
        // keep as string until valid; caller will validate on save
        coerced = newValue;
      }
    }
    onChange({ ...record, [key]: coerced });
  };

  const handleJsonChange = (text) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  if (mode === "json") {
    return (
      <div className="space-y-2">
        <Label>Raw JSON</Label>
        <Textarea
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          rows={20}
          className="font-mono text-xs"
          data-testid="admin-record-json-editor"
        />
        {jsonError && (
          <p className="text-xs text-red-600" data-testid="admin-json-error">
            Invalid JSON: {jsonError}
          </p>
        )}
      </div>
    );
  }

  const keys = Object.keys(record || {});
  if (keys.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No fields yet — switch to JSON mode to add fields.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
      {keys.map((key) => {
        const value = record[key];
        const type = detectType(value);
        const isReadOnly = key === "id" || key === "created_at";
        const testid = `field-${key}`;

        if (type === "boolean") {
          return (
            <div key={key} className="flex items-center justify-between p-3 border rounded-md">
              <Label htmlFor={testid} className="capitalize">
                {key.replace(/_/g, " ")}
              </Label>
              <Switch
                id={testid}
                checked={!!value}
                onCheckedChange={(v) => handleFieldChange(key, v, "boolean")}
                data-testid={testid}
              />
            </div>
          );
        }

        if (type === "textarea" || type === "json") {
          const displayValue =
            type === "json" ? JSON.stringify(value, null, 2) : String(value ?? "");
          return (
            <div key={key} className="md:col-span-2 space-y-1.5">
              <Label htmlFor={testid} className="capitalize">
                {key.replace(/_/g, " ")}{" "}
                {type === "json" && (
                  <span className="text-xs text-slate-500">(JSON)</span>
                )}
              </Label>
              <Textarea
                id={testid}
                value={displayValue}
                rows={type === "json" ? 6 : 4}
                readOnly={isReadOnly}
                onChange={(e) => handleFieldChange(key, e.target.value, type)}
                className={type === "json" ? "font-mono text-xs" : ""}
                data-testid={testid}
              />
            </div>
          );
        }

        // string or number
        return (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={testid} className="capitalize">
              {key.replace(/_/g, " ")}
            </Label>
            <Input
              id={testid}
              type={type === "number" ? "number" : "text"}
              value={value ?? ""}
              readOnly={isReadOnly}
              onChange={(e) => handleFieldChange(key, e.target.value, type)}
              data-testid={testid}
            />
          </div>
        );
      })}
    </div>
  );
};

// -------------------- Main component --------------------
const AdminDataManager = () => {
  const { collection: routeCollection } = useParams();
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(routeCollection || "users");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editMode, setEditMode] = useState("fields"); // fields | json
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  // Load collections list
  useEffect(() => {
    axios
      .get(`${API}/admin/collections`)
      .then((res) => setCollections(res.data))
      .catch(() => toast.error("Failed to load collections"));
  }, []);

  // Sync from URL param
  useEffect(() => {
    if (routeCollection && routeCollection !== selected) {
      setSelected(routeCollection);
    }
  }, [routeCollection, selected]);

  const fetchData = useCallback(
    async (slug, q = "") => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/${slug}`, {
          params: { q: q || undefined, limit: 500 },
        });
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        toast.error(`Failed to load ${slug}`);
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(selected, search);
    // Fetches only when the selected collection changes; search box triggers on Enter.
  }, [selected]);

  const columns = useMemo(() => {
    // Union of keys across visible items, take up to 6 columns for readability
    const seen = new Set();
    const ordered = [];
    for (const item of items) {
      for (const k of Object.keys(item)) {
        if (HIDDEN_COLS.has(k)) continue;
        if (!seen.has(k)) {
          seen.add(k);
          ordered.push(k);
        }
      }
    }
    // Prefer id first, name/title next
    const preferred = ["id", "email", "full_name", "name", "title", "zone_name"];
    const primary = preferred.filter((p) => seen.has(p));
    const rest = ordered.filter((k) => !primary.includes(k));
    return [...primary, ...rest].slice(0, 6);
  }, [items]);

  const openCreate = () => {
    // Use last item as a template (empty its values) to give admin a scaffold
    let template = {};
    if (items.length > 0) {
      const src = items[0];
      Object.keys(src).forEach((k) => {
        if (HIDDEN_COLS.has(k)) return;
        if (k === "id" || k === "created_at") return;
        const v = src[k];
        if (typeof v === "boolean") template[k] = false;
        else if (typeof v === "number") template[k] = 0;
        else if (Array.isArray(v)) template[k] = [];
        else if (v && typeof v === "object") template[k] = {};
        else template[k] = "";
      });
    }
    setEditRecord(template);
    setIsNew(true);
    setEditMode("fields");
    setEditOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord({ ...record });
    setIsNew(false);
    setEditMode("fields");
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editRecord || typeof editRecord !== "object") {
      toast.error("Invalid record");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/${selected}`, editRecord);
        toast.success("Record created");
      } else {
        await axios.put(`${API}/admin/${selected}/${editRecord.id}`, editRecord);
        toast.success("Record updated");
      }
      setEditOpen(false);
      fetchData(selected, search);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/${selected}/${deleteId}`);
      toast.success("Record deleted");
      setDeleteId(null);
      fetchData(selected, search);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Delete failed";
      toast.error(String(msg));
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/${selected}/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selected}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-data-manager">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Data Manager
          </h1>
          <p className="text-slate-600">
            Create, edit, and delete records across all collections.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger
              className="w-[220px]"
              data-testid="admin-collection-select"
            >
              <SelectValue placeholder="Select collection" />
            </SelectTrigger>
            <SelectContent>
              {collections.map((c) => (
                <SelectItem key={c.slug} value={c.slug} data-testid={`collection-option-${c.slug}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchData(selected, search)}
            data-testid="admin-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            data-testid="admin-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            className="bg-[#4E80FF] hover:bg-[#3d6de6]"
            onClick={openCreate}
            data-testid="admin-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchData(selected, search);
            }}
            className="pl-9"
            data-testid="admin-search-input"
          />
        </div>
        <Badge variant="outline" data-testid="admin-total-count">
          {total} records
        </Badge>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-700 border-t-transparent" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No records in{" "}
              <span className="font-medium capitalize">
                {selected.replace(/_/g, " ")}
              </span>
              . Click <span className="font-medium">New</span> to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((c) => (
                      <TableHead key={c} className="capitalize">
                        {c.replace(/_/g, " ")}
                      </TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id || JSON.stringify(item)} data-testid={`admin-row-${item.id}`}>
                      {columns.map((c) => (
                        <TableCell key={c} className="max-w-[240px] truncate">
                          {formatCellValue(item[c])}
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(item)}
                            data-testid={`admin-edit-${item.id}`}
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(item.id)}
                            data-testid={`admin-delete-${item.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / Create Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="admin-dialog-title">
              {isNew ? "Create" : "Edit"}{" "}
              <span className="capitalize">
                {selected.replace(/_/g, " ").replace(/s$/, "")}
              </span>
            </DialogTitle>
            <DialogDescription>
              {isNew
                ? "Fill in the fields below and click Save."
                : "Update fields and click Save."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-2">
            <Button
              type="button"
              variant={editMode === "fields" ? "default" : "outline"}
              size="sm"
              onClick={() => setEditMode("fields")}
              className={editMode === "fields" ? "bg-[#4E80FF]" : ""}
              data-testid="admin-mode-fields"
            >
              <ListChecks className="w-4 h-4 mr-1" /> Fields
            </Button>
            <Button
              type="button"
              variant={editMode === "json" ? "default" : "outline"}
              size="sm"
              onClick={() => setEditMode("json")}
              className={editMode === "json" ? "bg-[#4E80FF]" : ""}
              data-testid="admin-mode-json"
            >
              <Code2 className="w-4 h-4 mr-1" /> JSON
            </Button>
          </div>

          {editRecord && (
            <RecordForm
              record={editRecord}
              onChange={setEditRecord}
              mode={editMode}
            />
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              data-testid="admin-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-[#4E80FF] hover:bg-[#3d6de6]"
              onClick={handleSave}
              disabled={saving}
              data-testid="admin-save-btn"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The record{" "}
              <span className="font-mono">{deleteId}</span> will be permanently
              removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDataManager;
